import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  ButtonToolbar,
  CloseButton,
  Col,
  Form,
  InputGroup,
  Row
} from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";
import truncate from "lodash/truncate";
import { NotificationsLevel } from "../../lib/enums";
import api from "../../services/api";
import { show } from "../../notifications/notifications";
import MultipleFileSelector from "../../forms/components/MultipleFileSelector";
import TooltipIcon from "../TooltipIcon";
import Typography from "../typography/Typography";
import ReanalyzeForm from "../../modules/samples/components/reanalyze/reanalyze-form/ReanalyzeForm";
import {
  getLocaleForPlatform,
  INITIAL_RLCS_PARAMS
} from "../../modules/samples/components/Reanalyze";
import usePrepareReanalyzeData, {
  areAdditionalAnalysisTypesAvailable
} from "../../modules/header/hooks/usePrepareReanalyzeData";
import DisabledWrapper from "../../modules/yara/dashboard/grid/DisabledWrapper";
import { LayoutType, HandleUploadParams, AnalysisSelections } from "../@types";
import {
  getDynamicAnalysisList,
  INITIAL_FILE_UPLOAD_ANALYSIS_SELECTIONS,
  LAYOUT_TYPE
} from "./utils";
import { enums, route } from "../../shim/jsbridge";

type FileAnalysisFormProps = {
  closeModal: () => void;
  handleUpload: (args: HandleUploadParams) => void;
  onLayout?: (layout: LayoutType) => void;
};

type FileAnalysisFormState = {
  files: Array<File>;
  fileUrl: string;
};

const initialValues: FileAnalysisFormState = {
  files: [] as Array<File>,
  fileUrl: ""
};

const getRlCloudSandboxPlatform = (
  analysisSelections: AnalysisSelections,
  selectedPlatform: string
) => {
  if (analysisSelections.rl_dynamic_analysis) {
    return selectedPlatform;
  } else {
    return undefined;
  }
};

const FileAnalysisForm: React.FC<FileAnalysisFormProps> = ({
  closeModal,
  handleUpload,
  onLayout
}) => {
  const filePickerRef = useRef<HTMLInputElement>(null);
  const selectFile = (event: React.UIEvent) => {
    event.preventDefault();
    filePickerRef.current?.click();
  };
  const [filePassword, setFilePassword] = useState();
  const [additionalConfigurationOpen, setAdditionalConfigurationOpen] =
    useState(false);
  const [analysisSelections, setAnalysisSelections] = useState(
    INITIAL_FILE_UPLOAD_ANALYSIS_SELECTIONS
  );
  const [previousAnalysisSelections, setPreviousAnalysisSelections] = useState(
    INITIAL_FILE_UPLOAD_ANALYSIS_SELECTIONS
  );
  const [rlcsParams, setRlcsParams] = useState(INITIAL_RLCS_PARAMS);

  const { data: formData } = usePrepareReanalyzeData();

  useEffect(() => {
    if (formData) {
      if (formData.ti_cloud.autoupload && formData.ti_cloud.enabled) {
        setAnalysisSelections(prevState => ({ ...prevState, ti_cloud: true }));
      }
    }
  }, [formData]);

  const [values, setValues] = useState<FileAnalysisFormState>(initialValues);
  const { mutate: uploadFromUrl, isPending: isLoading } = useMutation({
    mutationFn: (data: FileAnalysisFormState) => {
      const localeForPlatform = getLocaleForPlatform(
        rlcsParams.locale,
        rlcsParams.platform
      );

      return api.uploadFromUrl(
        data.fileUrl,
        analysisSelections.ti_cloud,
        getRlCloudSandboxPlatform(analysisSelections, rlcsParams.platform),
        analysisSelections.rl_dynamic_analysis && !rlcsParams.interactive
          ? rlcsParams.geolocation
          : null,
        analysisSelections.rl_dynamic_analysis && !rlcsParams.interactive
          ? localeForPlatform
          : null,
        rlcsParams.interactive,
        getDynamicAnalysisList(analysisSelections)
      );
    },
    onSuccess: data => {
      const {
        data: { uploadIds }
      } = data ?? {};
      show({
        level: NotificationsLevel.SUCCESS,
        message: `Successfully started URL upload ${truncate(values.fileUrl, {
          length: 32
        })} `
      });

      const isRlInteractiveAnalysisSelected = rlcsParams.interactive;
      if (isRlInteractiveAnalysisSelected) {
        uploadIds?.forEach((uploadId: string) =>
          window.open(
            route("rl_interactive_analysis_for_upload", [uploadId]),
            "_blank"
          )
        );
      }
    },
    onError: (error?: { data: Array<string> }) => {
      const message = Object.values(error?.data || {}).join(" ");
      show({
        level: NotificationsLevel.ERROR,
        message: `${truncate(values.fileUrl, {
          length: 32
        })} upload failed${message ? `: ${message}` : ""}.`
      });
    }
  });
  const disableLocalFiles = Boolean(values.fileUrl);
  const disableUrl = Boolean(values.files?.length);

  const fileUploadIsEmpty = !(disableLocalFiles || disableUrl);

  const submitFileOrUrl = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (values.files.length) {
      const localeForPlatform = getLocaleForPlatform(
        rlcsParams.locale,
        rlcsParams.platform
      );

      handleUpload({
        files: values.files,
        password: filePassword,
        uploadToTicloud: analysisSelections.ti_cloud,
        rlCloudSandboxPlatform: getRlCloudSandboxPlatform(
          analysisSelections,
          rlcsParams.platform
        ),
        rlCloudSandboxGeolocation:
          analysisSelections.rl_dynamic_analysis && !rlcsParams.interactive
            ? rlcsParams.geolocation
            : null,
        rlCloudSandboxLocale:
          analysisSelections.rl_dynamic_analysis && !rlcsParams.interactive
            ? localeForPlatform
            : null,
        rlCloudSandboxInteractive: rlcsParams.interactive,
        dynamicAnalysis: getDynamicAnalysisList(analysisSelections)
      });
    } else {
      uploadFromUrl(values);
    }
    closeModal();
  };

  const openAdditionalConfigurationStep = () => {
    if (fileUploadIsEmpty) {
      return;
    }

    setPreviousAnalysisSelections(analysisSelections);
    setAdditionalConfigurationOpen(true);

    onLayout?.(
      areAdditionalAnalysisTypesAvailable(formData)
        ? LAYOUT_TYPE.LG
        : LAYOUT_TYPE.MD
    );
  };

  const closeAdditionalConfigurationStep = () => {
    onLayout?.(LAYOUT_TYPE.XL);
    setAdditionalConfigurationOpen(false);
    setAnalysisSelections(previousAnalysisSelections);
  };

  const fileUploadStep = (
    <Form onSubmit={submitFileOrUrl}>
      <Row>
        <Col xs={5}>
          <Form.Group
            as={Row}
            className="mb-25"
            controlId="browse-local-files-input"
          >
            <Form.Label
              className={clsx(
                disableLocalFiles && "file-analysis-label-disabled",
                "file-analysis-label-1",
                "mt-15"
              )}
            >
              Browse Local Files
            </Form.Label>

            <Col xs={9}>
              <MultipleFileSelector
                filePickerRef={filePickerRef}
                files={Array.from(values.files ?? [])}
                setFiles={newFiles => setValues({ ...values, files: newFiles })}
                placeholder="Local File"
                isDisabled={disableLocalFiles}
                required
              />
            </Col>

            <Col xs={3}>
              <Button
                variant="prompt-secondary"
                onClick={selectFile}
                disabled={disableLocalFiles}
              >
                Browse Files
              </Button>
            </Col>
          </Form.Group>
        </Col>

        <Col className="file-analysis-border" xs={2}>
          <div className="file-analysis-border-text">OR</div>
        </Col>

        <Col xs={5}>
          <Form.Group
            as={Row}
            className="mb-25"
            controlId="enter-file-url-text-box"
          >
            <Form.Label
              className={clsx(
                disableUrl && "file-analysis-label-disabled",
                "file-analysis-label-1",
                "mt-25"
              )}
            >
              <div className="flex">
                <span>Enter a File URL</span>

                <TooltipIcon
                  icon="info-circle"
                  message="Provide a link to the file you want to analyze, e.g. https://www.example.org/file.pdf. Both HTTP and HTTPS protocols are supported."
                  className="file-analysis-tooltip-icon"
                />
              </div>
            </Form.Label>

            <InputGroup>
              <Form.Control
                type="text"
                className="file-analysis-text-input"
                placeholder="Enter URL"
                spellCheck="false"
                disabled={disableUrl}
                onChange={e => {
                  setValues({
                    ...values,
                    fileUrl: e.target.value
                  });
                }}
                value={values.fileUrl}
                required
              />

              <CloseButton
                className="file-analysis-text-clear"
                disabled={disableUrl}
                onClick={() => setValues({ ...values, fileUrl: "" })}
              />
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <hr className={"my-25"} />

      <Row>
        <Col md={12}>
          <DisabledWrapper disabled={fileUploadIsEmpty}>
            <Typography variant={"subtitle5"} tag={"span"}>
              Uploaded samples will follow your current analysis settings. To
              make a one-time adjustment for this analysis,{" "}
            </Typography>
            <Typography
              variant={"subtitle5"}
              tag={"span"}
              className={clsx("text-decoration-underline", {
                "cursor-pointer text-color-blue": !fileUploadIsEmpty
              })}
              onClick={openAdditionalConfigurationStep}
            >
              click here.
            </Typography>
          </DisabledWrapper>
        </Col>
      </Row>

      <hr className={"my-25"} />

      <ButtonToolbar className="file-analysis-buttons-right mt-0">
        <Button type="reset" variant="outline-primary" onClick={closeModal}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading}>
          Submit
        </Button>
      </ButtonToolbar>
    </Form>
  );

  const additionalConfigurationStep = (
    <ReanalyzeForm
      initial={analysisSelections}
      values={analysisSelections}
      displayWarningMessage={values.files?.length > 5}
      popupWindowData={formData}
      onCancel={closeAdditionalConfigurationStep}
      onValues={(updatedValues: AnalysisSelections) => {
        if (updatedValues.rl_dynamic_analysis) {
          setAnalysisSelections({ ...updatedValues, ti_cloud: true });
        } else {
          setAnalysisSelections(updatedValues);
        }
      }}
      onSubmit={submitFileOrUrl}
      onRlcsParamsChange={setRlcsParams}
      rlcsParams={rlcsParams}
      tcAnalysisDisabled
      submitButtonLabel={"Submit"}
      onFilePasswordChange={setFilePassword}
      ticloudAnalysisDisabled={analysisSelections.rl_dynamic_analysis}
      interactiveAnalysisDisabled={values.files?.length > 1}
    />
  );

  return additionalConfigurationOpen
    ? additionalConfigurationStep
    : fileUploadStep;
};

export default FileAnalysisForm;
